import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Spacings, mediaQuery } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import Rating from '@comp/Rating/Rating';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeedbackOptions } from '@actions/auth';
import { submitSubscriptionFeedback } from '@api/fetchers/auth';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { getFeedbackOptions } from '@selectors/users';
import RateYourExperienceDetails from '@comp/RateYourExperience/RateYourExperienceDetails';
import { getIsSubmittingFeedback } from '@selectors/auth';
import useWindowSize from '@hooks/useWindowSize';
import H4 from '@base/H4/H4';
import P1 from '@base/P1/P1';
import { useSnackbar } from '@stores/snackbar';
import {
  BATCH_GROUP_DETAIL,
  PLATFORM_BATCH_GROUP,
  PLATFORM_SUBSCRIPTION_RATING,
  SUBSCRIPTION_FEEDBACK_CARD_CLICKED,
  SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED,
  SUBSCRIPTION_FEEDBACK_CARD_VIEWED
} from '@cont/BatchGroup/constants/events';

const OuterWrapper = styled.div`
  grid-column: span 16;
  margin-top: ${Spacings.SPACING_12B};
`;

const Wrapper = styled.div`
  background-color: var(--color-base-1);
  display: flex;
  justify-content: space-between;
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  width: 72%;
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_0B} ${Spacings.SPACING_6B}
    ${Spacings.SPACING_6B};
  z-index: 1;
  ${mediaQuery.uptoTablet} {
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B}
      ${Spacings.SPACING_4B} ${Spacings.SPACING_4B};
  }
`;

const StyledRating = styled(Rating)`
  align-items: center;
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoTablet} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const Title = styled(H4)``;

const Description = styled(P1)`
  margin-top: ${Spacings.SPACING_2B};
  color: var(--color-text-secondary);
`;

const ImageWrapper = styled.div`
  position: absolute;
  right: 0;
  height: inherit;
`;
const FeedbackImage = styled.img`
  height: 100%;
`;

const SubscriptionFeedbackCard = ({
  feedbackData = {},
  goalUID,
  isShowFeedback
}) => {
  const [showFeedbackSection, setShowFeedbackSection] =
    useState(isShowFeedback);
  const [ratingValue, setRatingValue] = useState(0);
  const [drawerState, setDrawerState] = useState(false);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const feedbackOptions = useSelector(getFeedbackOptions);
  const isSubmittingFeedback = useSelector(getIsSubmittingFeedback);
  const { isMobile } = useWindowSize();
  const dispatch = useDispatch();
  const { open: showSnackbar } = useSnackbar();

  const itemInfo = feedbackData?.item_info;
  const objectUID = itemInfo?.object_uid;
  const feedbackImage = isMobile
    ? itemInfo?.images_url?.mweb_url
    : itemInfo?.images_url?.web_url;

  useEffect(() => {
    dispatch(fetchFeedbackOptions(objectUID, itemInfo?.feedback_type));
  }, [dispatch, itemInfo?.feedback_type, objectUID]);

  const sendEvent = (eventName) => {
    segment.track(eventName, {
      ...goalSegmentData,
      card_type: PLATFORM_SUBSCRIPTION_RATING,
      entity_content_type: PLATFORM_BATCH_GROUP,
      entity_uid: objectUID,
      entity_title: itemInfo?.entity_title,
      last_primary_source: BATCH_GROUP_DETAIL
    });
  };

  useEffect(() => {
    sendEvent(SUBSCRIPTION_FEEDBACK_CARD_VIEWED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (value) => () => {
    setRatingValue(value);
    setDrawerState(true);
    sendEvent(SUBSCRIPTION_FEEDBACK_CARD_CLICKED);
  };

  const handleCloseRatingDialog = (closeData) => {
    setRatingValue(0);
    const hasSubmittedFeedback = closeData?.hasSubmittedFeedback;
    if (hasSubmittedFeedback) {
      showSnackbar({
        message: 'Your feedback has been submitted',
        type: 'success'
      });
      setShowFeedbackSection(false);
    }
    setDrawerState(false);
  };

  const setRatingOnCard = (value) => {
    setRatingValue(value);
  };

  if (!showFeedbackSection) return null;
  const { feedback_type, header, sub_text, entity_title } = itemInfo || {};

  return (
    <OuterWrapper>
      <RateYourExperienceDetails
        ratingValue={ratingValue}
        setRatingValue={setRatingOnCard}
        feedBackDetails={feedbackOptions}
        submitFeedback={submitSubscriptionFeedback}
        objectUID={objectUID}
        isSubmittingFeedback={isSubmittingFeedback}
        extraData={{ goalUID, feedbackType: feedback_type }}
        drawerState={drawerState}
        onClose={handleCloseRatingDialog}
        source={SUBSCRIPTION_FEEDBACK_CARD_SUBMITTED}
        goalUID={goalUID}
        entityTitle={entity_title}
        entityContentType={PLATFORM_BATCH_GROUP}
        lastPrimarySource={BATCH_GROUP_DETAIL}
      />
      <Wrapper>
        <ContentWrapper>
          <Title>{header}</Title>
          <Description>{sub_text}</Description>
          <StyledRating
            key={ratingValue}
            value={ratingValue}
            setRating={handleClick}
          />
        </ContentWrapper>
        {feedbackImage && (
          <ImageWrapper>
            <FeedbackImage src={feedbackImage} alt="feedback_image" />
          </ImageWrapper>
        )}
      </Wrapper>
    </OuterWrapper>
  );
};

export default SubscriptionFeedbackCard;

SubscriptionFeedbackCard.propTypes = { isShowFeedback: PropTypes.bool };
SubscriptionFeedbackCard.defaultProps = { isShowFeedback: false };
